import React from 'react';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import type { SxProps, Theme } from '@mui/material/styles';
import { useT } from '../../../i18n';

/**
 * Fila de filtros de las pantallas de Procesos: rejilla que pasa a una columna en teléfono
 * (`minmax(min(220px, 100%), 1fr)`), dentro de una tarjeta con borde.
 */
export const FilterBar: React.FC<{ children: React.ReactNode; sx?: SxProps<Theme>; plain?: boolean }> = ({ children, sx, plain }) => {
  const grid = (
    <Box
      sx={{
        display: 'grid',
        gap: 2,
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(220px, 100%), 1fr))',
        alignItems: 'start',
      }}
    >
      {children}
    </Box>
  );
  if (plain) return grid;
  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, sm: 2.5 }, mb: 2, ...(sx as object) }}>
      {grid}
    </Paper>
  );
};

type Option = { id: number | string; name: string };

interface SelectFieldProps {
  id: string;
  label: string;
  value: string | number | null | undefined;
  options: Option[];
  onChange: (value: string) => void;
  /** Texto de la opción vacía (por ejemplo "Todas"); sin él no hay opción vacía. */
  emptyLabel?: string;
  disabled?: boolean;
  required?: boolean;
  helperText?: React.ReactNode;
}

/** Lista desplegable de la librería de pantallas: etiqueta arriba, ancho completo y sin saltos al cargar. */
export const SelectField: React.FC<SelectFieldProps> = ({ id, label, value, options, onChange, emptyLabel, disabled, required, helperText }) => {
  // El valor debe existir entre las opciones; si aún no han llegado, se muestra vacío.
  const current = value === null || value === undefined ? '' : String(value);
  const known = current === '' || options.some((o) => String(o.id) === current);
  return (
    <TextField
      id={id}
      select
      fullWidth
      label={label}
      value={known ? current : ''}
      onChange={(e) => onChange(String(e.target.value))}
      disabled={disabled}
      required={required}
      helperText={helperText}
      SelectProps={{ displayEmpty: Boolean(emptyLabel) }}
      InputLabelProps={emptyLabel ? { shrink: true } : undefined}
    >
      {emptyLabel !== undefined && <MenuItem value="">{emptyLabel}</MenuItem>}
      {options.map((o) => (
        <MenuItem key={o.id} value={String(o.id)}>
          {o.name}
        </MenuItem>
      ))}
    </TextField>
  );
};

interface OfficeRouteFieldsProps {
  offices: Option[];
  routes: Option[];
  officeId: string | number | null | undefined;
  routeId: string | number | null | undefined;
  onOfficeChange: (value: string) => void;
  onRouteChange: (value: string) => void;
  /** Con opción "Todas/Todos" (filtros de historial). */
  allowAll?: boolean;
  idPrefix?: string;
}

/** Sede y ruta, en ese orden; la ruta se habilita al elegir la sede. */
export const OfficeRouteFields: React.FC<OfficeRouteFieldsProps> = ({
  offices,
  routes,
  officeId,
  routeId,
  onOfficeChange,
  onRouteChange,
  allowAll,
  idPrefix = 'filter',
}) => {
  const t = useT();
  return (
    <>
      <SelectField
        id={`${idPrefix}-office`}
        label={allowAll ? t('common.office') : t('common.selectOffice')}
        value={officeId}
        options={offices}
        onChange={onOfficeChange}
        emptyLabel={allowAll ? t('common.all') : undefined}
      />
      <SelectField
        id={`${idPrefix}-route`}
        label={allowAll ? t('common.route') : t('common.selectRoute')}
        value={routeId}
        options={routes}
        onChange={onRouteChange}
        emptyLabel={allowAll ? t('common.all') : undefined}
        disabled={!officeId}
      />
    </>
  );
};

/** Sección de formulario: tarjeta con borde y título. */
export const Section: React.FC<{ title?: React.ReactNode; action?: React.ReactNode; children: React.ReactNode; sx?: SxProps<Theme> }> = ({
  title,
  action,
  children,
  sx,
}) => (
  <Paper variant="outlined" sx={{ p: { xs: 2, sm: 2.5 }, minWidth: 0, ...(sx as object) }}>
    {(title || action) && (
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        {title && (
          <Box component="h2" sx={{ m: 0, typography: 'h6', flex: '1 1 auto', minWidth: 0 }}>
            {title}
          </Box>
        )}
        {action}
      </Box>
    )}
    {children}
  </Paper>
);
